import React, { useState } from "react";
import { Avatar, Box, IconButton, Stack, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import ShareIcon from "@mui/icons-material/Share";
import HoverRating from "../../components/HoverRating";
import ListMenu from "../../components/ListMenu";
import { StyledLink } from "../../components/CustomMUI";
import { ExpandableTypography } from "../../components/ExpandableTypography";
import { shareLink } from "../../utils/share";
import { buildContentPath, SHARE_ORIGIN } from "../../utils/detailPaths";
import { formatCalendarDate, getStatusOptions } from "../../utils/utils";
import {
  AVATAR_SIZE,
  EntryRow,
  LINE_HEIGHT,
  NOTE_LINE_HEIGHT,
  ReactionChips,
  ROW_GAP,
} from "./journalEntryLayout";

const AVATAR_COLUMN = { xs: 36, sm: 42 }; // avatar's largest (sm+) size + gap to the content column

/**
 * Read-only counterpart to JournalEntry: one entry from someone else — in the
 * Following tab's list, or on its own in FocusedEntry. Reuses JournalEntry's
 * row/gutter primitives (see
 * journalEntryLayout.js) so the two views line up, but always renders the
 * mobile (single-column-of-rows) layout — there's no per-viewport desktop
 * promotion here since there's no more-button-sharing top row to save space.
 */
export default function FriendJournalEntry({ entry, person }) {
  const [moreMenuAnchorEl, setMoreMenuAnchorEl] = useState(null);
  const statusOptions = getStatusOptions();

  const status = entry.status;
  const statusIcon = statusOptions[status].icon;

  const hasRating = entry?.rating != null;
  const hasReactions = entry?.reactions?.length > 0;
  const hasNotes = Boolean(entry?.notes?.trim());

  const shareUrl =
    entry &&
    SHARE_ORIGIN +
      buildContentPath({
        contentType: entry.content_type,
        contentId: entry.content_id,
        title: entry.content_title,
      }) +
      `?entry=${entry.journal_id}`;

  const moreButton = entry ? (
    <IconButton
      size="small"
      sx={{ p: 0.5 }}
      onClick={(event) => setMoreMenuAnchorEl(event.currentTarget)}
    >
      <MoreHorizIcon />
    </IconButton>
  ) : null;

  // Each block carries its own slot metrics for the same reason as
  // JournalEntry's contentBlocks — a note's line box is shorter than a row of
  // controls, and a note top-aligns and wraps where the others centre.
  const contentBlocks = [
    hasRating && {
      key: "rating",
      node: <HoverRating value={entry.rating} readOnly />,
      slotHeight: LINE_HEIGHT,
      contentAlign: "center",
    },
    hasReactions && {
      key: "reactions",
      node: <ReactionChips reactions={entry.reactions} />,
      slotHeight: LINE_HEIGHT,
      contentAlign: "center",
    },
    // Friends' notes can run long with no owner-side incentive to keep them
    // short — clamp with a Show more/less toggle rather than letting one
    // entry's note push everyone else down the list.
    hasNotes && {
      key: "notes",
      node: (
        <ExpandableTypography
          text={entry.notes}
          lineClamp={3}
          variant="body2"
          whiteSpace="pre-wrap"
        />
      ),
      slotHeight: NOTE_LINE_HEIGHT,
      contentAlign: "flex-start",
    },
  ].filter(Boolean);

  // A bare entry would otherwise be just its header line, with nothing to say
  // why. Only reachable on the @username tab (FocusedEntry) — the Following
  // list only ever holds feed-worthy entries. Ratings and reactions only exist
  // on finished entries, so other statuses can only be missing a note.
  if (entry && contentBlocks.length === 0) {
    const missing =
      status === "finished" ? "rating, reaction or note" : "note";
    contentBlocks.push({
      key: "empty",
      node: (
        <Typography variant="body2" color="text.disabled">
          Marked as {statusOptions[status].label.toLowerCase()} with no{" "}
          {missing}
        </Typography>
      ),
      slotHeight: NOTE_LINE_HEIGHT,
      contentAlign: "flex-start",
    });
  }

  return (
    <Box sx={{ display: "flex" }}>
      <Box sx={{ width: AVATAR_COLUMN, flexShrink: 0, pt: 0.25 }}>
        <Box component={RouterLink} to={`/u/${person.username}`}>
          <Avatar
            src={person.avatar_url}
            alt={person.username}
            imgProps={{ referrerPolicy: "no-referrer" }}
            sx={{ width: AVATAR_SIZE, height: AVATAR_SIZE }}
          />
        </Box>
      </Box>
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Stack direction="column" spacing={ROW_GAP}>
          {/* Username, status icon and date share one top line — the more
              button (share) lives in its trailing gutter. Every row below
              uses no leading gutter of its own, so rating/reactions/notes
              all start flush under the username rather than indented to
              make room for a status icon that's already up here. */}
          <EntryRow
            leadingWidth={0}
            slotHeight={LINE_HEIGHT}
            trailing={moreButton}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ minWidth: 0, flexGrow: 1 }}
            >
              {/* xs only: fills whatever space the fixed-width icon/date
                  group (below) doesn't need, then truncates — rather than a
                  fixed cutoff, which would be wrong for some combination of
                  date length and viewport width. minWidth:0 is required for
                  a flex item to shrink below its content's natural width at
                  all. sm+ has room for the full username and simply sits
                  left-aligned with the icon/date following after it. */}
              <StyledLink
                component={RouterLink}
                to={`/u/${person.username}`}
                variant="body2"
                sx={{
                  fontWeight: 500,
                  flexGrow: { xs: 1, sm: 0 },
                  minWidth: 0,
                  overflow: { xs: "hidden", sm: "visible" },
                  textOverflow: { xs: "ellipsis", sm: "clip" },
                  whiteSpace: "nowrap",
                  display: "block",
                }}
              >
                @{person.username}
              </StyledLink>
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                // pr only matters at xs, where flexGrow above pushes this
                // group flush against the more button's trailing gutter —
                // EntryRow doesn't otherwise add a gap there.
                sx={{ flexShrink: 0, pr: { xs: 1, sm: 0 } }}
              >
                {statusIcon}
                <Typography
                  variant="body2"
                  sx={{ color: "text.primary", fontWeight: 500 }}
                >
                  {entry.date ? formatCalendarDate(entry.date) : "Undated"}
                </Typography>
              </Stack>
            </Stack>
          </EntryRow>

          {contentBlocks.map((block) => (
            <EntryRow
              key={block.key}
              leadingWidth={0}
              slotHeight={block.slotHeight}
              contentAlign={block.contentAlign}
            >
              {block.node}
            </EntryRow>
          ))}
        </Stack>
      </Box>

      {entry && (
        <ListMenu
          anchorEl={moreMenuAnchorEl}
          onClose={() => setMoreMenuAnchorEl(null)}
          onSelect={() => () => {
            setMoreMenuAnchorEl(null);
            shareLink({
              url: shareUrl,
              title: entry.content_title,
              copiedMessage:
                "Link copied! Paste it anywhere to share this entry",
            });
          }}
          options={[
            { value: "share", label: "Share entry", icon: <ShareIcon /> },
          ]}
          align="right"
        />
      )}
    </Box>
  );
}
